/**
 * HUB-H — Onglet Équipe du Centre Activité (Carlos-only, SEC-01).
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { isPrimaryAdmin } from '../../../utils/roleRouting';
import useActiviteEquipe from '../../../hooks/activite/useActiviteEquipe';
import ActiviteEquipeAccessBanner from '../ActiviteEquipeAccessBanner';
import ActiviteEquipeKpiStrip from '../ActiviteEquipeKpiStrip';
import ActiviteEquipeSectionTabs from '../ActiviteEquipeSectionTabs';
import ActiviteEquipeSeverityFilters from '../ActiviteEquipeSeverityFilters';
import ActiviteEquipeAuditCard from '../ActiviteEquipeAuditCard';
import ActiviteEquipeAlertCard from '../ActiviteEquipeAlertCard';
import ActiviteEquipeSecurityCard from '../ActiviteEquipeSecurityCard';
import { ACTIVITE_TAB_IDS } from '../../../utils/activite/activityTabs';
import { EQUIPE_SECTIONS } from '../../../utils/equipe/equipeDisplay';

function CardList({ items, loading, emptyMessage, testId, renderItem }) {
  if (loading) {
    return <div className="flex justify-center py-12"><div className="loading-spinner" /></div>;
  }
  if (!items.length) {
    return (
      <p className="text-center py-12 text-[var(--rm-gray-500)]" data-testid={testId}>
        {emptyMessage}
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid={`${testId}-list`}>
      {items.map(renderItem)}
    </div>
  );
}

export default function ActiviteEquipeTab({ filters }) {
  const { api, user } = useAuth();
  const isCarlosOnly = isPrimaryAdmin(user);
  const [activeSection, setActiveSection] = useState(EQUIPE_SECTIONS.OVERVIEW);

  const {
    recentLogs,
    logs,
    alerts,
    securityEvents,
    teamActivity,
    kpis,
    loading,
    error,
    severityFilter,
    setSeverityFilter,
  } = useActiviteEquipe(api, filters, { enabled: isCarlosOnly });

  const sectionCounts = useMemo(() => ({
    [EQUIPE_SECTIONS.OVERVIEW]: recentLogs.length,
    [EQUIPE_SECTIONS.AUDIT]: logs.length,
    [EQUIPE_SECTIONS.ALERTES]: alerts.length,
    [EQUIPE_SECTIONS.SECURITE]: securityEvents.length,
  }), [recentLogs, logs, alerts, securityEvents]);

  if (!isCarlosOnly) {
    return (
      <div
        className="rounded-xl border p-8 text-center"
        style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
        data-testid="activite-equipe-denied"
      >
        <p className="text-sm text-[var(--rm-gray-400)]">Accès réservé — SEC-01</p>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-4">
      {teamActivity.length > 0 && (
        <div data-testid="activite-equipe-team-activity">
          <h3 className="text-sm font-semibold text-[var(--rm-white)] mb-2">Activité équipe (période)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {teamActivity.map(({ username, count }) => (
              <div
                key={username}
                className="rounded-xl border px-3 py-2 min-h-[44px]"
                style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
                data-testid={`activite-equipe-user-${username}`}
              >
                <div className="text-xs text-[var(--rm-gray-500)] truncate">{username}</div>
                <div className="font-bold text-[var(--rm-white)]">{count} action(s)</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="activite-equipe-overview-alerts">
          <h3 className="text-sm font-semibold text-[var(--rm-red-light)]">Alertes prioritaires</h3>
          {alerts.slice(0, 3).map((a) => (
            <ActiviteEquipeAlertCard key={`${a.type}-${a.username}-${a.message}`} alert={a} />
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-[var(--rm-white)]">20 actions récentes</h3>
          <Link
            to="/admin/audit"
            className="text-xs text-[var(--rm-gray-400)] hover:text-[var(--rm-white)] inline-flex items-center gap-1 min-h-[44px] px-2"
            data-testid="activite-equipe-link-full-audit"
          >
            Audit complet
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <CardList
          items={recentLogs}
          loading={loading}
          emptyMessage="Aucune action sur la période"
          testId="activite-equipe-recent"
          renderItem={(log) => <ActiviteEquipeAuditCard key={log.id} log={log} />}
        />
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case EQUIPE_SECTIONS.AUDIT:
        return (
          <div className="space-y-4">
            <ActiviteEquipeSeverityFilters value={severityFilter} onChange={setSeverityFilter} />
            <CardList
              items={logs}
              loading={loading}
              emptyMessage="Aucun log d'audit"
              testId="activite-equipe-audit"
              renderItem={(log) => <ActiviteEquipeAuditCard key={log.id} log={log} />}
            />
          </div>
        );
      case EQUIPE_SECTIONS.ALERTES:
        return (
          <CardList
            items={alerts}
            loading={loading}
            emptyMessage="Aucune alerte détectée"
            testId="activite-equipe-alerts"
            renderItem={(a) => (
              <ActiviteEquipeAlertCard key={`${a.type}-${a.username}-${a.message}`} alert={a} />
            )}
          />
        );
      case EQUIPE_SECTIONS.SECURITE:
        return (
          <div className="space-y-4">
            <p className="text-xs text-[var(--rm-gray-500)]" data-testid="activite-equipe-security-hint">
              Connexions, tentatives échouées, IP bannies et événements sécurité — consultation uniquement.
            </p>
            <CardList
              items={securityEvents}
              loading={loading}
              emptyMessage="Aucun événement sécurité sur la période"
              testId="activite-equipe-security"
              renderItem={(e) => (
                <ActiviteEquipeSecurityCard key={e.id || `${e.type}-${e.created_at}`} event={e} />
              )}
            />
          </div>
        );
      default:
        return renderOverview();
    }
  };

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 space-y-4"
      style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
      data-testid="activite-equipe-tab"
    >
      <ActiviteEquipeAccessBanner />

      <ActiviteEquipeKpiStrip kpis={kpis} />

      <ActiviteEquipeSectionTabs
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        counts={sectionCounts}
      />

      {error && (
        <div
          className="rounded-xl border p-4 text-sm text-[var(--rm-red-light)]"
          style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)' }}
          data-testid="activite-equipe-error"
        >
          {error}
        </div>
      )}

      {renderSection()}

      <span className="sr-only" data-testid="activite-equipe-tab-id">{ACTIVITE_TAB_IDS.EQUIPE}</span>
    </div>
  );
}
